import { useCallback, useState } from 'react'
import logo from '../../assets/img/logo.svg'
import Spinner from '../../components/Spinner/Spinner.jsx'
import styles from './CDR.module.scss'

const CDR_SERVICE_URL = process.env.REACT_APP_CDR_SERVICE_URL ?? ''

const CDR = () => {
  const [isSubmitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: ''
  })
  const [focus, setFocus] = useState({
    email: false,
    name: false,
    phone: false
  })
  const [validity, setValidity] = useState({
    email: true,
    phone: true
  })
  const [dirty, setDirty] = useState({
    email: false,
    phone: false
  })

  const validateEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }, [])

  const validatePhone = useCallback((phone) => {
    if (!phone) {
      return true // Optional field
    }
    const phoneRegex = /^\+?[\d\s-()]{10,}$/
    return phoneRegex.test(phone)
  }, [])

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      phone: ''
    })
    setDirty({
      email: false,
      phone: false
    })
    setValidity({
      email: true,
      phone: true
    })
    setFocus({
      email: false,
      name: false,
      phone: false
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (name === 'email' || name === 'phone') {
      setDirty(prev => ({
        ...prev,
        [name]: true
      }))
      
      setValidity(prev => ({
        ...prev,
        [name]: name === 'email' ? validateEmail(value) : validatePhone(value)
      }))
    }
  }

  const handleFocus = (field) => {
    setFocus(prev => ({
      ...prev,
      [field]: true
    }))
  }

  const handleBlur = (field) => {
    setFocus(prev => ({
      ...prev,
      [field]: false
    }))
  }

  const handleSubmit = async(e) => {
    e.preventDefault()
    if (!formData.email || !validity.email || !validity.phone) {
      return
    }
    setSubmitting(true)
    try {
      const response = await fetch(`${CDR_SERVICE_URL}/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          ...(formData.name && {name: formData.name}),
          ...(formData.phone && {phone: formData.phone})
        })
      })

      if (response.ok) {
        const data = await response.json()
        resetForm()
        alert('Successfully enrolled in CDR')
        window.open(data.authUrl, '_blank')
      } else {
        throw new Error('CDR service is not available')
      }
  } catch (error) {
    console.error({error})
    alert('CDR service is not available')
  } finally {
    setSubmitting(false)
  }
  }

  const renderInput = (field, label, type = 'text') => (
    <div className={`${styles.inputWrapper} ${!validity[field] && dirty[field] ? styles.error : ''}`}>
      <input
        id={field}
        name={field}
        type={type}
        className={`${styles.input} ${!validity[field] && dirty[field] ? styles.errorInput : ''}`}
        value={formData[field]}
        onChange={handleInputChange}
        onFocus={() => handleFocus(field)}
        onBlur={() => handleBlur(field)}
        aria-invalid={!validity[field]}
      />
      <label
        htmlFor={field}
        className={`${styles.floatingLabel} ${(focus[field] || formData[field]) ? styles.active : ''} ${!validity[field] && dirty[field] ? styles.error : ''}`}
      >
        {label}
      </label>
      {!validity[field] && dirty[field] && (
        <span className={styles.errorMessage}>
          {field === 'email' ? 'Please enter a valid email address' : 'Please enter a valid phone number'}
        </span>
      )}
    </div>
  )

  const Overlay = () => (
    <>
    <div role='dialog' className={styles.overlay} />
      <Spinner color="#ea5449" />
  </>
  )

  return (
    <main className={styles.wrapper}>
      <img
        src={logo}
        width={186}
        height={116}
        className={styles.logo}
        alt="birdwood logo"
      />

      <h1 className={styles.header}>Consumer Data Right enrollment</h1>
      <p className={styles.description}>Enter your details to enroll in the Consumer Data Right program</p>
      
      <form onSubmit={handleSubmit} className={styles.form}>
        {renderInput('email', 'Email*', 'email')}
        {renderInput('name', 'Name')}
        {renderInput('phone', 'Phone', 'tel')}
        
        <button
          type="submit"
          className={styles.button}
          disabled={!formData.email || !validity.email || !validity.phone}
        >
          Submit
        </button>
      </form>
      {isSubmitting && <Overlay />}
    </main>
  )
}

export default CDR
